import { CellRow } from "@/components/CellGrid/CellRow";
import { Grid } from "@/components/CellGrid/Grid";
import { Itemization } from "@/components/CellGrid/Itemization";
import { TitleRow } from "@/components/CellGrid/TitleRow";
import { Waterfall } from "@/components/CellGrid/Waterfall";
import { Doc } from "@/components/Doc/Doc";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import {
  isConventionalLoanTypeAtom,
  isExternalEquityAtom,
  isLineOfCreditIncludedAtom,
} from "@/financeModels/owasco/general";
import {
  cashFlowGrowthCapExMolecules,
  cashFlowGrowthCapExTotalMolecule,
  operatingGrowthCapExMolecules,
  operatingGrowthCapExTotalMolecule,
} from "@/financeModels/owasco/growthCapEx";
import {
  amortOfDealExpensesMolecule,
  annualMaintenanceCapExMolecule,
  buyersCompMolecule,
  companySaleDebtPayoffMolecule,
  companysTaxRateMolecule,
  costOfGoodsSoldItemization,
  depreciationAndAmortizationMolecule,
  distributableFreeCashFlowMolecule,
  ebitdaMarginsMolecule,
  ebitdaMolecule,
  goodwillDepreciationAndAmortizationMolecule,
  grossMarginsMolecule,
  grossProfitMolecule,
  grossRevenueItemization,
  incomeTaxesMolecule,
  lineOfCreditEndBalanceMolecule,
  lineOfCreditInterestPaymentMolecule,
  lineOfCreditPaymentMolecule,
  lineOfCreditPrincipalPaymentMolecule,
  lineOfCreditStartBalanceMolecule,
  netIncomeMolecule,
  operatingExpensesItemization,
  operatingIncomeMolecule,
  proceedsFromCompanySaleMolecule,
  sba7aLoanExpenseBalloonPaymentsMolecule,
  sba7aLoanExpenseEndingBalanceMolecule,
  sba7aLoanExpenseExtraPaymentsMolecule,
  sba7aLoanExpenseInterestPaymentMolecule,
  sba7aLoanExpensePrincipalMolecule,
  sba7aLoanExpenseStartingBalanceMolecule,
  sba7aLoanPaymentMolecule,
  sellersNoteExpenseBalloonPaymentsMolecule,
  sellersNoteExpenseEndingBalanceMolecule,
  sellersNoteExpenseExtraPaymentsMolecule,
  sellersNoteExpenseInterestPaymentMolecule,
  sellersNoteExpensePrincipalMolecule,
  sellersNoteExpenseStartingBalanceMolecule,
  sellersNoteLoanPaymentMoleculeWithBalloon,
  taxableIncomeMolecule,
  totalDebtServiceMolecule,
  totalDebtServiceWithSaleDebtPayoffMolecule,
  totalEquityInvestorsDistributionsMolecule,
  totalEquityInvestorsProceedsFromCompanySaleMolecule,
  totalEquityPreferredDividendsMolecule,
  totalEquityPrincipalRepaymentsMolecule,
  totalEquityProfitDistributionsMolecule,
  unleveredFreeCashFlowMolecule,
  useableFreeCashFlowMolecule,
} from "@/financeModels/owasco/projections";
import {
  waterfallPreferredDividendsAccruedMolecule,
  waterfallPreferredDividendsAccruedPrefBeginBalMolecule,
  waterfallPreferredDividendsAccruedPrefEndBalMolecule,
  waterfallPreferredDividendsPaidMolecule,
  waterfallPrincipalRepaymentCapitalBeginBalMolecule,
  waterfallPrincipalRepaymentCapitalEndBalMolecule,
  waterfallPrincipalRepaymentCumulativePrincipalRepaidMolecule,
} from "@/financeModels/owasco/projectionsWaterfallTables";
import { Divider } from "@mantine/core";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { IterationRow } from "./IterationRow";
import { WaterfallDebugTables } from "./WaterfallDebugTables";

export const DataGrid = () => {
  const isConventionalLoanType = useAtomValue(isConventionalLoanTypeAtom);
  let tabYIndex = 0;
  const [waterfallDebugTablesShowCount, setWaterfallDebugTablesShowCount] =
    useState(0);

  const cashFlowGapExMolecules = useAtomValue(cashFlowGrowthCapExMolecules);
  const operatingGapExMolecules = useAtomValue(operatingGrowthCapExMolecules);
  const isLineOfCreditIncluded = useAtomValue(isLineOfCreditIncludedAtom);
  const isExternalEquity = useAtomValue(isExternalEquityAtom);

  return (
    <Grid w={"100%"} h={"100%"}>
      <IterationRow />
      <TitleRow>Company Financials</TitleRow>
      <Waterfall>
        <Itemization
          itemizationAtom={grossRevenueItemization}
          tabY={tabYIndex++}
          label={
            <LabelExplainer
              showIcon={false}
              label={`Gross Revenue`}
              explanation={
                <Doc path="@/docs/pages/model/projections/gross-revenue.mdx" />
              }
            />
          }
          secondaryLabel={"Gross Revenue"}
        />

        <Itemization
          itemizationAtom={costOfGoodsSoldItemization}
          label={
            <LabelExplainer
              showIcon={false}
              label={`Cost of Goods Sold (COGS)`}
              explanation={
                <Doc path="@/docs/pages/model/projections/cost-of-goods-sold.mdx" />
              }
            />
          }
          secondaryLabel={"COGS"}
          tabY={tabYIndex++}
        />
        <CellRow
          molecule={grossProfitMolecule}
          bold
          label={
            <Waterfall.Formula
              explanation={
                <Doc path={"@/docs/pages/model/projections/gross-profit.mdx"} />
              }
            >
              Gross Profit
            </Waterfall.Formula>
          }
          tabY={tabYIndex++}
        />
      </Waterfall>
      <Waterfall lineColor="transparent">
        <CellRow
          molecule={grossMarginsMolecule}
          label={
            <LabelExplainer
              showIcon={false}
              label={`Gross Margins`}
              explanation={
                <Doc path="@/docs/pages/model/projections/gross-margins.mdx" />
              }
            />
          }
          tabY={tabYIndex++}
          variant="transparent"
        />
      </Waterfall>
      <Divider my="xs" />
      <Waterfall>
        <Itemization
          itemizationAtom={operatingExpensesItemization}
          label={
            <LabelExplainer
              showIcon={false}
              label={`Operating Expenses`}
              explanation={
                <Doc path="@/docs/pages/model/projections/operating-expenses.mdx" />
              }
            />
          }
          secondaryLabel={"OpEx ex. Buyer's Comp"}
          tabY={tabYIndex++}
        />

        <CellRow
          molecule={buyersCompMolecule}
          label={
            <LabelExplainer
              showIcon={false}
              label={`Buyer's Compensation`}
              explanation={
                <Doc path="@/docs/pages/model/projections/buyers-compensation.mdx" />
              }
            />
          }
          tabY={tabYIndex++}
        />

        <CellRow
          molecule={ebitdaMolecule}
          bold
          label={
            <Waterfall.Formula
              explanation={
                <Doc path={"@/docs/pages/model/projections/ebitda.mdx"} />
              }
            >
              EBITDA
            </Waterfall.Formula>
          }
          tabY={tabYIndex++}
        />
      </Waterfall>
      <Waterfall lineColor="transparent">
        <CellRow
          molecule={ebitdaMarginsMolecule}
          label={
            <LabelExplainer
              showIcon={false}
              label={`EBITDA Margins`}
              explanation={
                <Doc path="@/docs/pages/model/projections/ebitda-margins.mdx" />
              }
            />
          }
          tabY={tabYIndex++}
          labelWidth={150}
          variant="transparent"
        />
      </Waterfall>
      <Divider my="xs" />
      <Waterfall>
        <Waterfall.Collapse
          molecule={depreciationAndAmortizationMolecule}
          label={"Depreciation & Amortization"}
          explanation={
            <Doc path="@/docs/pages/model/projections/depreciation-and-amortization.mdx" />
          }
          tabY={tabYIndex++}
        >
          <CellRow
            molecule={goodwillDepreciationAndAmortizationMolecule}
            label={
              <LabelExplainer
                showIcon={false}
                label={`Goodwill (D & A)`}
                explanation={
                  <Doc path="@/docs/pages/model/projections/amortization-of-goodwill.mdx" />
                }
              />
            }
            tabY={tabYIndex++}
          />
          <CellRow
            molecule={amortOfDealExpensesMolecule}
            label={
              <LabelExplainer
                showIcon={false}
                label={`Deal Expenses (D & A)`}
                explanation={
                  <Doc path="@/docs/pages/model/projections/amortization-of-deal-expenses.mdx" />
                }
              />
            }
            tabY={tabYIndex++}
          />

          <Waterfall.Collapse
            molecule={operatingGrowthCapExTotalMolecule}
            label={"Growth CapEx (D & A)"}
            explanation={
              <Doc
                path={
                  "@/docs/pages/model/projections/growth-capEx-cashflow.mdx"
                }
              />
            }
            tabY={tabYIndex++}
          >
            {operatingGapExMolecules.map((item) => (
              <CellRow
                key={item.label}
                label={item.label}
                tabY={tabYIndex++}
                molecule={item.molecule}
              />
            ))}
          </Waterfall.Collapse>
        </Waterfall.Collapse>

        <CellRow
          molecule={operatingIncomeMolecule}
          bold
          label={
            <Waterfall.Formula
              explanation={
                <Doc
                  path={"@/docs/pages/model/projections/operating-income.mdx"}
                />
              }
            >
              Operating Income
            </Waterfall.Formula>
          }
          tabY={tabYIndex++}
        />
      </Waterfall>
      <Divider my="xs" />
      <Waterfall>
        <Waterfall.Collapse
          label={"Income Taxes"}
          explanation={
            <Doc path={"@/docs/pages/model/projections/income-taxes.mdx"} />
          }
          tabY={tabYIndex++}
          molecule={incomeTaxesMolecule}
        >
          <CellRow
            tabY={tabYIndex++}
            molecule={sba7aLoanExpenseInterestPaymentMolecule}
            label={
              isConventionalLoanType ? (
                <LabelExplainer
                  showIcon={false}
                  label={`Interest - Senior Debt`}
                  explanation={
                    <Doc path="@/docs/pages/model/projections/senior-debt-interest.mdx" />
                  }
                />
              ) : (
                <LabelExplainer
                  showIcon={false}
                  label={`Interest - SBA 7(a) Loan`}
                  explanation={
                    <Doc path="@/docs/pages/model/projections/sba-7a-loan-interest.mdx" />
                  }
                />
              )
            }
          />
          <CellRow
            tabY={tabYIndex++}
            molecule={sellersNoteExpenseInterestPaymentMolecule}
            label={
              <LabelExplainer
                showIcon={false}
                label={`Interest - Seller's Note`}
                explanation={
                  <Doc path="@/docs/pages/model/projections/sellers-note-interest.mdx" />
                }
              />
            }
          />

          {isLineOfCreditIncluded && (
            <CellRow
              tabY={tabYIndex++}
              molecule={lineOfCreditInterestPaymentMolecule}
              label={
                <LabelExplainer
                  showIcon={false}
                  label={`Interest - Line of Credit`}
                  explanation={
                    <Doc path="@/docs/pages/model/projections/line-of-credit-interest.mdx" />
                  }
                />
              }
            />
          )}

          <CellRow
            tabY={tabYIndex++}
            molecule={taxableIncomeMolecule}
            bold
            label={
              <Waterfall.Formula
                explanation={
                  <Doc
                    path={"@/docs/pages/model/projections/taxable-income.mdx"}
                  />
                }
              >
                Taxable Income
              </Waterfall.Formula>
            }
          />
          <CellRow
            tabY={tabYIndex++}
            molecule={companysTaxRateMolecule}
            label={
              <LabelExplainer
                showIcon={false}
                label={`Company's Tax Rate`}
                explanation={
                  <Doc path="@/docs/pages/model/projections/companys-tax-rates.mdx" />
                }
              />
            }
          />
        </Waterfall.Collapse>

        <CellRow
          molecule={sba7aLoanExpenseInterestPaymentMolecule}
          label={
            isConventionalLoanType ? (
              <LabelExplainer
                showIcon={false}
                label={`Interest - Senior Debt`}
                explanation={
                  <Doc path="@/docs/pages/model/projections/senior-debt-interest.mdx" />
                }
              />
            ) : (
              <LabelExplainer
                showIcon={false}
                label={`Interest - Senior Debt`}
                explanation={
                  <Doc path="@/docs/pages/model/projections/sba-7a-loan-interest.mdx" />
                }
              />
            )
          }
          tabY={tabYIndex++}
        />
        <CellRow
          molecule={sellersNoteExpenseInterestPaymentMolecule}
          label={
            <LabelExplainer
              showIcon={false}
              label={`Interest - Seller's Note`}
              explanation={
                <Doc path="@/docs/pages/model/projections/sellers-note-interest.mdx" />
              }
            />
          }
          tabY={tabYIndex++}
        />

        {isLineOfCreditIncluded && (
          <CellRow
            tabY={tabYIndex++}
            molecule={lineOfCreditInterestPaymentMolecule}
            label={
              <LabelExplainer
                showIcon={false}
                label={`Interest - Line of Credit`}
                explanation={
                  <Doc path="@/docs/pages/model/projections/line-of-credit-interest.mdx" />
                }
              />
            }
          />
        )}

        <CellRow
          molecule={netIncomeMolecule}
          bold
          label={
            <Waterfall.Formula
              explanation={
                <Doc path={"@/docs/pages/model/projections/net-income.mdx"} />
              }
            >
              Net Income
            </Waterfall.Formula>
          }
          tabY={tabYIndex++}
        />
      </Waterfall>

      <Divider my="xs" />

      <TitleRow>
        <span onClick={() => setWaterfallDebugTablesShowCount((p) => p + 1)}>
          Debt Service
        </span>
      </TitleRow>

      <Waterfall>
        <Waterfall.Collapse
          label={
            isConventionalLoanType
              ? "Senior Debt Loan Total"
              : "SBA 7(a) Loan Total"
          }
          explanation={
            !isConventionalLoanType ? (
              <Doc
                path={
                  "@/docs/pages/model/projections/sba7a-loan-total-payments.mdx"
                }
              />
            ) : (
              <Doc
                path={
                  "@/docs/pages/model/projections/senior-debt-loan-total.mdx"
                }
              />
            )
          }
          tabY={tabYIndex++}
          molecule={sba7aLoanPaymentMolecule}
        >
          <CellRow
            molecule={sba7aLoanExpenseStartingBalanceMolecule}
            label={
              isConventionalLoanType
                ? "Senior Debt - Beginning Balance"
                : "SBA 7(a) - Beginning Balance"
            }
            tabY={tabYIndex++}
            variant="transparent"
            labelWidth={240}
          />
          <CellRow
            molecule={sba7aLoanExpenseInterestPaymentMolecule}
            label={
              !isConventionalLoanType ? (
                <LabelExplainer
                  showIcon={false}
                  label={`SBA 7(a) - Interest Payment`}
                  explanation={
                    <Doc path="@/docs/pages/model/projections/sba-7a-loan-interest.mdx" />
                  }
                />
              ) : (
                <LabelExplainer
                  showIcon={false}
                  label={`Senior Debt - Interest Payment`}
                  explanation={
                    <Doc path="@/docs/pages/model/projections/senior-debt-interest.mdx" />
                  }
                />
              )
            }
            tabY={tabYIndex++}
          />
          <CellRow
            molecule={sba7aLoanExpensePrincipalMolecule}
            label={
              isConventionalLoanType ? (
                <LabelExplainer
                  showIcon={false}
                  label={`Senior Debt - Principal`}
                  explanation={
                    <Doc path="@/docs/pages/model/projections/senior-debt-principle.mdx" />
                  }
                />
              ) : (
                <LabelExplainer
                  showIcon={false}
                  label={`SBA 7(a) Loan - Principal`}
                  explanation={
                    <Doc path="@/docs/pages/model/projections/sba-7a-loan-principal.mdx" />
                  }
                />
              )
            }
            tabY={tabYIndex++}
          />
          <CellRow
            molecule={sba7aLoanExpenseExtraPaymentsMolecule}
            label={
              !isConventionalLoanType ? (
                <LabelExplainer
                  showIcon={false}
                  label={`SBA 7(a) - Extra Payments`}
                  explanation={
                    <Doc path="@/docs/pages/model/projections/sba-7a-loan-extra-payments.mdx" />
                  }
                />
              ) : (
                <LabelExplainer
                  showIcon={false}
                  label={`Senior Debt - Extra Payments`}
                  explanation={
                    <Doc path="@/docs/pages/model/projections/senior-debt-extra-payments.mdx" />
                  }
                />
              )
            }
            tabY={tabYIndex++}
          />

          <CellRow
            molecule={sba7aLoanExpenseBalloonPaymentsMolecule}
            label={
              !isConventionalLoanType ? (
                <LabelExplainer
                  showIcon={false}
                  label={`SBA 7(a) - Balloon Payments`}
                  explanation={
                    <Doc path="@/docs/pages/model/projections/sba-7a-loan-balloon.mdx" />
                  }
                />
              ) : (
                <LabelExplainer
                  showIcon={false}
                  label={`Senior Debt - Balloon Payments`}
                  explanation={
                    <Doc path="@/docs/pages/model/projections/senior-debt-balloon.mdx" />
                  }
                />
              )
            }
            tabY={tabYIndex++}
          />
          <CellRow
            molecule={sba7aLoanExpenseEndingBalanceMolecule}
            label={
              isConventionalLoanType
                ? "Senior Debt - Ending Balance"
                : "SBA 7(a) - Ending Balance"
            }
            tabY={tabYIndex++}
            variant="transparent"
            labelWidth={220}
          />
        </Waterfall.Collapse>
        {isLineOfCreditIncluded && (
          <Waterfall.Collapse
            label={"Line of Credit"}
            explanation={
              <Doc
                path={
                  "@/docs/pages/model/projections/line-of-credit-total-payments.mdx"
                }
              />
            }
            tabY={tabYIndex++}
            molecule={lineOfCreditPaymentMolecule}
          >
            <CellRow
              molecule={lineOfCreditStartBalanceMolecule}
              label={"Line of Credit - Beginning Balance"}
              tabY={tabYIndex++}
              variant="transparent"
              labelWidth={250}
            />
            <CellRow
              molecule={lineOfCreditInterestPaymentMolecule}
              label={
                <LabelExplainer
                  showIcon={false}
                  label={`Line of Credit - Interest Payment`}
                  explanation={
                    <Doc path="@/docs/pages/model/projections/line-of-credit-interest.mdx" />
                  }
                />
              }
              tabY={tabYIndex++}
            />
            <CellRow
              molecule={lineOfCreditPrincipalPaymentMolecule}
              label={
                <LabelExplainer
                  showIcon={false}
                  label={`Line of Credit - Principal Payment`}
                  explanation={
                    <Doc path="@/docs/pages/model/projections/line-of-credit-principal.mdx" />
                  }
                />
              }
              tabY={tabYIndex++}
            />
            <CellRow
              molecule={lineOfCreditEndBalanceMolecule}
              label={"Line of Credit - Ending Balance"}
              tabY={tabYIndex++}
              variant="transparent"
              labelWidth={230}
            />
          </Waterfall.Collapse>
        )}
        <Waterfall.Collapse
          label={"Seller's Note - Loan"}
          explanation={
            <Doc
              path={
                "@/docs/pages/model/projections/sellers-note-total-payments.mdx"
              }
            />
          }
          tabY={tabYIndex++}
          molecule={sellersNoteLoanPaymentMoleculeWithBalloon}
        >
          <CellRow
            molecule={sellersNoteExpenseStartingBalanceMolecule}
            label={"Seller's Note - Beginning Balance"}
            tabY={tabYIndex++}
            variant="transparent"
            labelWidth={250}
          />
          <CellRow
            molecule={sellersNoteExpenseInterestPaymentMolecule}
            label={
              <LabelExplainer
                showIcon={false}
                label={`Seller's Note - Interest Payment`}
                explanation={
                  <Doc path="@/docs/pages/model/projections/sellers-note-interest.mdx" />
                }
              />
            }
            tabY={tabYIndex++}
          />
          <CellRow
            molecule={sellersNoteExpensePrincipalMolecule}
            label={
              <LabelExplainer
                showIcon={false}
                label={`Seller's Note - Principal Payment`}
                explanation={
                  <Doc path="@/docs/pages/model/projections/sellers-note-principal.mdx" />
                }
              />
            }
            tabY={tabYIndex++}
          />
          <CellRow
            molecule={sellersNoteExpenseExtraPaymentsMolecule}
            label={
              <LabelExplainer
                showIcon={false}
                label={`Seller's Note - Extra Payments`}
                explanation={
                  <Doc path="@/docs/pages/model/projections/sellers-note-extra-payments.mdx" />
                }
              />
            }
            tabY={tabYIndex++}
          />
          <CellRow
            molecule={sellersNoteExpenseBalloonPaymentsMolecule}
            label={
              <LabelExplainer
                showIcon={false}
                label={`Seller's Note - Balloon Payments`}
                explanation={
                  <Doc path="@/docs/pages/model/projections/sellers-note-balloon.mdx" />
                }
              />
            }
            tabY={tabYIndex++}
          />
          <CellRow
            molecule={sellersNoteExpenseEndingBalanceMolecule}
            label={"Seller's Note - Ending Balance"}
            tabY={tabYIndex++}
            variant="transparent"
            labelWidth={230}
          />
        </Waterfall.Collapse>
        <CellRow
          molecule={totalDebtServiceMolecule}
          bold
          label={
            <Waterfall.Formula
              explanation={
                <Doc
                  path={"@/docs/pages/model/projections/total-debt-service.mdx"}
                />
              }
            >
              Total Debt Service
            </Waterfall.Formula>
          }
          tabY={tabYIndex++}
        />
      </Waterfall>

      <Divider my="xs" />

      {waterfallDebugTablesShowCount > 5 && <WaterfallDebugTables />}

      <TitleRow>Cash Flow Metrics & Distributions</TitleRow>

      <Waterfall.Collapse
        molecule={unleveredFreeCashFlowMolecule}
        label={"Unlevered Free Cash Flow"}
        explanation={
          <Doc
            path={"@/docs/pages/model/projections/unlevered-free-cash-flow.mdx"}
          />
        }
        tabY={tabYIndex++}
      >
        <CellRow
          bold
          label={
            <Waterfall.Formula
              explanation={
                <Doc path={"@/docs/pages/model/projections/ebitda.mdx"} />
              }
            >
              EBITDA
            </Waterfall.Formula>
          }
          tabY={tabYIndex++}
          molecule={ebitdaMolecule}
        />
        <CellRow
          bold
          label={
            <Waterfall.Formula
              explanation={
                <Doc path={"@/docs/pages/model/projections/income-taxes.mdx"} />
              }
            >
              Income Taxes
            </Waterfall.Formula>
          }
          tabY={tabYIndex++}
          molecule={incomeTaxesMolecule}
        />

        <Waterfall.Collapse
          molecule={cashFlowGrowthCapExTotalMolecule}
          label={"Growth CapEx"}
          explanation={
            <Doc
              path={"@/docs/pages/model/projections/growth-capEx-cashflow.mdx"}
            />
          }
          tabY={tabYIndex++}
        >
          {cashFlowGapExMolecules.map((item) => (
            <CellRow
              key={item.label}
              label={item.label}
              tabY={tabYIndex++}
              molecule={item.molecule}
            />
          ))}
        </Waterfall.Collapse>
        <CellRow
          label={
            <LabelExplainer
              showIcon={false}
              label={`Annual Maintenance CapEx`}
              explanation={
                <Doc path="@/docs/pages/model/projections/annual-maintenance-capex.mdx" />
              }
            />
          }
          tabY={tabYIndex++}
          molecule={annualMaintenanceCapExMolecule}
        />
        <CellRow
          label={
            <LabelExplainer
              showIcon={false}
              label={`Gross Proceeds from Company Sale`}
              explanation={
                <Doc path="@/docs/pages/model/projections/proceeds-from-company-sale.mdx" />
              }
            />
          }
          tabY={tabYIndex++}
          molecule={proceedsFromCompanySaleMolecule}
        />
      </Waterfall.Collapse>

      <Divider my="xs" />

      <Waterfall.Collapse
        molecule={distributableFreeCashFlowMolecule}
        label={"Distributable Free Cash Flow"}
        explanation={
          <Doc
            path={
              "@/docs/pages/model/projections/distributable-free-cash-flow.mdx"
            }
          />
        }
        tabY={tabYIndex++}
      >
        <CellRow
          molecule={unleveredFreeCashFlowMolecule}
          bold
          label={
            <Waterfall.Formula
              explanation={
                <Doc
                  path={
                    "@/docs/pages/model/projections/unlevered-free-cash-flow.mdx"
                  }
                />
              }
            >
              Unlevered Free Cash Flow
            </Waterfall.Formula>
          }
          tabY={tabYIndex++}
        />

        <Waterfall.Collapse
          molecule={totalDebtServiceWithSaleDebtPayoffMolecule}
          label={"Total Debt Service"}
          explanation={
            <Doc
              path={"@/docs/pages/model/projections/total-debt-service.mdx"}
            />
          }
          tabY={tabYIndex++}
        >
          <CellRow
            molecule={totalDebtServiceMolecule}
            label={
              <LabelExplainer
                showIcon={false}
                label={"Regular Debt Service"}
                explanation={
                  <Doc
                    path={
                      "@/docs/pages/model/projections/regular-debt-service.mdx"
                    }
                  />
                }
              />
            }
            tabY={tabYIndex++}
          />
          <CellRow
            molecule={companySaleDebtPayoffMolecule}
            label={
              <LabelExplainer
                showIcon={false}
                label={"Debt Payoff (Company Sale)"}
                explanation={
                  <Doc
                    path={"@/docs/pages/model/projections/debt-payoff.mdx"}
                  />
                }
              />
            }
            tabY={tabYIndex++}
          />
        </Waterfall.Collapse>
      </Waterfall.Collapse>
      <Divider my="xs" />
      <Waterfall.Collapse
        molecule={useableFreeCashFlowMolecule}
        label={"Usable Free Cash Flow"}
        explanation={
          <Doc
            path={"@/docs/pages/model/projections/usable-free-cash-flow.mdx"}
          />
        }
        tabY={tabYIndex++}
      >
        <CellRow
          molecule={distributableFreeCashFlowMolecule}
          label={
            <LabelExplainer
              showIcon={false}
              label={"Distributable Free Cash Flow"}
              explanation={
                <Doc path="@/docs/pages/model/projections/distributable-free-cash-flow.mdx" />
              }
            />
          }
          tabY={tabYIndex++}
        />

        {isExternalEquity && (
          <>
            <Waterfall.Collapse
              molecule={totalEquityInvestorsDistributionsMolecule}
              label={"Equity Investor Distributions"}
              explanation={
                <Doc
                  path={
                    "@/docs/pages/model/projections/equity-investor-distributions.mdx"
                  }
                />
              }
              tabY={tabYIndex++}
            >
              <Waterfall.Collapse
                label={"Preferred Dividends"}
                explanation={
                  <Doc path="@/docs/pages/model/projections/equity-investor-prefered-dividends.mdx" />
                }
                tabY={tabYIndex++}
                molecule={totalEquityPreferredDividendsMolecule}
              >
                <CellRow
                  molecule={
                    waterfallPreferredDividendsAccruedPrefBeginBalMolecule
                  }
                  label={"Starting Balance"}
                  tabY={tabYIndex++}
                  variant="transparent"
                  labelWidth={250}
                />
                <CellRow
                  molecule={waterfallPreferredDividendsPaidMolecule}
                  label={
                    <LabelExplainer
                      showIcon={false}
                      label={`Current Period`}
                      explanation={
                        <Doc path="@/docs/pages/model/projections/preferred-dividends-current-period.mdx" />
                      }
                    />
                  }
                  tabY={tabYIndex++}
                />
                <CellRow
                  molecule={waterfallPreferredDividendsAccruedMolecule}
                  label={
                    <LabelExplainer
                      showIcon={false}
                      label={`Accrued Amount`}
                      explanation={
                        <Doc path="@/docs/pages/model/projections/preferred-dividends-accrued-amount.mdx" />
                      }
                    />
                  }
                  tabY={tabYIndex++}
                />
                <CellRow
                  molecule={
                    waterfallPreferredDividendsAccruedPrefEndBalMolecule
                  }
                  label={"Ending Balance"}
                  tabY={tabYIndex++}
                  variant="transparent"
                  labelWidth={230}
                />
              </Waterfall.Collapse>
              <Waterfall.Collapse
                label={"Principal Repayments"}
                explanation={
                  <Doc path="@/docs/pages/model/projections/equity-investor-principal-repayments.mdx" />
                }
                tabY={tabYIndex++}
                molecule={totalEquityPrincipalRepaymentsMolecule}
              >
                <CellRow
                  molecule={waterfallPrincipalRepaymentCapitalBeginBalMolecule}
                  label={"Starting Balance"}
                  tabY={tabYIndex++}
                  variant="transparent"
                  labelWidth={250}
                />
                <CellRow
                  molecule={
                    waterfallPrincipalRepaymentCumulativePrincipalRepaidMolecule
                  }
                  label={
                    <LabelExplainer
                      showIcon={false}
                      label={`Paid Down`}
                      explanation={
                        <Doc path="@/docs/pages/model/projections/principal-repayments-paid-down.mdx" />
                      }
                    />
                  }
                  tabY={tabYIndex++}
                />
                <CellRow
                  molecule={waterfallPrincipalRepaymentCapitalEndBalMolecule}
                  label={"Ending Balance"}
                  tabY={tabYIndex++}
                  variant="transparent"
                  labelWidth={230}
                />
              </Waterfall.Collapse>
              <CellRow
                molecule={totalEquityProfitDistributionsMolecule}
                label={
                  <LabelExplainer
                    showIcon={false}
                    label={"Profit Distributions"}
                    explanation={
                      <Doc path="@/docs/pages/model/projections/equity-investor-profit-distributions.mdx" />
                    }
                  />
                }
                tabY={tabYIndex++}
              />
              <CellRow
                molecule={totalEquityInvestorsProceedsFromCompanySaleMolecule}
                label={
                  <LabelExplainer
                    showIcon={false}
                    label={"Investors’ Proceeds from Company Sale"}
                    explanation={
                      <Doc path="@/docs/pages/model/projections/investor-proceeds-from-company-sale.mdx" />
                    }
                  />
                }
                tabY={tabYIndex++}
              />
            </Waterfall.Collapse>
          </>
        )}
      </Waterfall.Collapse>

      <Divider mb={"calc(25vh + 100px)"} />
    </Grid>
  );
};
